import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';

const FEATURES = [
    ['🌙', 'Replies out of hours'],
    ['🦷', 'Tags treatments'],
    ['📅', 'Acknowledges bookings'],
    ['💬', 'Answers FAQs'],
    ['🚨', 'Escalates emergencies'],
];

// Gentle wave for the robot emoji — like a hand-wave hello.
const wave = keyframes`
    0%, 60%, 100% { transform: rotate(0deg); }
    10% { transform: rotate(14deg); }
    20% { transform: rotate(-8deg); }
    30% { transform: rotate(14deg); }
    40% { transform: rotate(-4deg); }
    50% { transform: rotate(10deg); }
`;

// Subtle slide-up + fade-in for the title and description.
const slideUp = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// Pill pop-in — used by features with a stagger delay so they cascade.
const popIn = keyframes`
    from { opacity: 0; transform: translateY(8px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// Gradient shimmer behind the title — a slow drifting tint that gives
// the headline a sense of life without being noisy.
const shimmer = keyframes`
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
`;

export default function WelcomeStep() {
    return (
        <Box>
            {/* Inline title row — robot emoji waves on entry, title slides up,
                gradient shimmer drifts across the gradient text fill. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '4px' }}>
                <Typography sx={{
                    fontSize: 22,
                    transformOrigin: '70% 80%',
                    animation: `${wave} 2.4s ease-in-out 0.3s infinite`,
                }}>
                    🤖
                </Typography>
                <Typography sx={{
                    fontSize: 16, fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.2px',
                    backgroundImage: 'linear-gradient(90deg, #0D0D0D 0%, #00A76F 50%, #0D0D0D 100%)',
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: `${slideUp} 0.45s ease-out both, ${shimmer} 6s linear 0.45s infinite`,
                }}>
                    Meet your WhatsApp AI
                </Typography>
            </Box>
            <Typography sx={{
                fontSize: 12, color: '#7A7A7A', lineHeight: 1.5, mb: 1.5,
                animation: `${slideUp} 0.5s ease-out 0.1s both`,
            }}>
                Handles routine messages so your team focuses on what needs a human touch.
            </Typography>
            {/* 2-column compact feature grid — staggered pop-in cascades
                left-to-right, top-to-bottom (~50 ms per pill). */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '6px',
            }}>
                {FEATURES.map(([emoji, text], i) => (
                    <Box key={text} sx={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        p: '6px 8px', borderRadius: '8px',
                        backgroundColor: '#F7F7F7',
                        minWidth: 0,
                        animation: `${popIn} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
                        animationDelay: `${0.18 + i * 0.06}s`,
                        transition: 'background-color 0.2s, transform 0.2s',
                        '&:hover': {
                            backgroundColor: '#EFF6F2',
                            transform: 'translateY(-1px)',
                        },
                    }}>
                        <Typography sx={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{emoji}</Typography>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: '#3D3D3D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {text}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}


